// src/components/AdminNavMenu.tsx
//
// Navigation for the admin portal (/admin). The portal outgrew a flat tab row —
// eleven tabs wrapped onto two lines and gave no sense of which ones belong
// together — so navigation is a hamburger-opened drawer whose entries are
// grouped by what you're actually trying to do (money, fulfillment, catalog,
// plumbing).
//
// The drawer is a modal dialog: focus moves into it on open, Escape closes it,
// and the backdrop is click-to-dismiss (matching ConfirmationModal, WCAG 2.1.2
// / 2.4.3). Picking a tab closes it — the portal is single-tab-at-a-time, so
// leaving the drawer open would just cover the content you asked for.

import React, { useEffect, useRef } from "react";

export type AdminTab =
  | "dashboard"
  | "pricing"
  | "orders"
  | "traffic"
  | "attention"
  | "returns"
  | "reviews"
  | "promos"
  | "products"
  | "printful"
  | "feeds"
  | "tax";

type TabEntry = { id: AdminTab; label: string; hint: string };
type TabGroup = { title: string; tabs: TabEntry[] };

/**
 * Grouping rationale — these are the four questions an owner opens the portal
 * with, in the order they're usually asked:
 *  1. "How is the business doing?"  (backward-looking money + the forward-
 *     looking pricing half, plus where the traffic and promo sales came from)
 *  2. "What needs doing right now?" (per-order work queues)
 *  3. "What are we selling?"        (catalog state)
 *  4. "Is the plumbing OK?"         (nexus/tax blobs — rarely touched)
 */
export const ADMIN_TAB_GROUPS: TabGroup[] = [
  {
    title: "Business",
    tabs: [
      { id: "dashboard", label: "Dashboard", hint: "Revenue & margin" },
      { id: "pricing", label: "Pricing & Costs", hint: "Recommended prices" },
      { id: "traffic", label: "Traffic", hint: "Visits & attribution" },
      { id: "promos", label: "Promo Codes", hint: "Codes & salespeople" },
    ],
  },
  {
    title: "Orders & Customers",
    tabs: [
      { id: "orders", label: "Orders", hint: "Uncaptured → shipped" },
      { id: "attention", label: "Needs Attention", hint: "Alerts to clear" },
      { id: "returns", label: "Returns", hint: "Refund requests" },
      { id: "reviews", label: "Reviews", hint: "Moderation queue" },
    ],
  },
  {
    title: "Catalog",
    tabs: [
      {
        id: "products",
        label: "Product Management",
        hint: "Visibility, sync & controls",
      },
      {
        id: "printful",
        label: "Printful Availability",
        hint: "Supplier health",
      },
    ],
  },
  {
    title: "System",
    tabs: [
      { id: "feeds", label: "Feed Health", hint: "Meta feed & sitemap" },
      { id: "tax", label: "Tax Blobs", hint: "Economic-nexus totals" },
    ],
  },
];

const ALL_TABS: TabEntry[] = ADMIN_TAB_GROUPS.flatMap((g) => g.tabs);

/** Every tab id, for validating the `?tab=` deep link. */
export const ADMIN_TAB_IDS: AdminTab[] = ALL_TABS.map((t) => t.id);

/** Label + group for a tab, for the closed-state header. */
export function adminTabCrumb(tab: AdminTab): { group: string; label: string } {
  const group = ADMIN_TAB_GROUPS.find((g) => g.tabs.some((t) => t.id === tab));
  const entry = ALL_TABS.find((t) => t.id === tab);
  return { group: group?.title ?? "", label: entry?.label ?? "" };
}

interface AdminNavMenuProps {
  tab: AdminTab;
  onSelect: (tab: AdminTab) => void;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const AdminNavMenu: React.FC<AdminNavMenuProps> = ({
  tab,
  onSelect,
  isOpen,
  onOpen,
  onClose,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const crumb = adminTabCrumb(tab);

  // Escape closes; focus enters the panel on open and returns to the hamburger
  // on close so keyboard users aren't dumped at the top of the document.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const trigger = triggerRef.current;
    document.addEventListener("keydown", onKeyDown);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      trigger?.focus();
    };
  }, [isOpen, onClose]);

  const select = (next: AdminTab) => {
    onSelect(next);
    onClose();
  };

  return (
    <>
      {/* Closed state: hamburger + where you currently are. */}
      <div className="flex items-center gap-3 border-b border-accent pb-3">
        <button
          ref={triggerRef}
          type="button"
          onClick={onOpen}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label="Open admin navigation"
          className="flex items-center gap-2 rounded-md border border-accent px-3 py-2 text-sm font-semibold opensans-text text-brown-dark hover:bg-brown/5 transition-colors"
        >
          <span aria-hidden="true" className="flex flex-col gap-[3px]">
            <span className="block h-[2px] w-4 bg-brown-dark" />
            <span className="block h-[2px] w-4 bg-brown-dark" />
            <span className="block h-[2px] w-4 bg-brown-dark" />
          </span>
          Menu
        </button>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-gray-500 opensans-text">
            {crumb.group}
          </p>
          <p className="text-sm font-semibold text-brown-dark opensans-text truncate">
            {crumb.label}
          </p>
        </div>
      </div>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={onClose}
            aria-hidden="true"
          />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Admin navigation"
            tabIndex={-1}
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] overflow-y-auto bg-white shadow-xl focus:outline-none"
          >
            <div className="flex items-center justify-between border-b border-accent px-4 py-3">
              <span className="text-sm font-semibold text-brown-dark opensans-text">
                Admin Portal
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close admin navigation"
                className="text-gray-500 hover:text-brown text-xl leading-none px-2"
              >
                &times;
              </button>
            </div>

            <nav className="py-2">
              {ADMIN_TAB_GROUPS.map((group) => (
                <div key={group.title} className="py-2">
                  <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 opensans-text">
                    {group.title}
                  </p>
                  <ul>
                    {group.tabs.map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          onClick={() => select(t.id)}
                          aria-current={tab === t.id ? "page" : undefined}
                          className={`w-full text-left px-4 py-2 opensans-text transition-colors ${
                            tab === t.id
                              ? "bg-brown/10 border-l-4 border-brown text-brown-dark"
                              : "border-l-4 border-transparent text-gray-600 hover:bg-brown/5 hover:text-brown"
                          }`}
                        >
                          <span className="block text-sm font-semibold">
                            {t.label}
                          </span>
                          <span className="block text-xs text-gray-500">
                            {t.hint}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default AdminNavMenu;
